import { MainPageWithButtons } from "../../Everou/_components";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { PMSLocationDetailSelector } from "./PMSLocationDetailSelector";
import { LocationInfo } from '../../Everou/_models/LocationInfo';
import { Selectors } from '../../Everou/_reducers/app.reducer';
import { useEffect, useState } from "react";
import { PMSLocationDetailViewModes } from "../_constants/pmsLocationsViewMode.constants";
import { PMSLocationLogs } from "./PMSLocationLogs";
import { PMSLocationAuditLogs } from "./PMSLocationAuditLogs";
import { PMSLocationsActions } from "../_actions/PMSLocationsActions";
import { viewModeActions } from "../_actions/viewMode.actions";
import { PMSLocationDevices } from "./PMSLocationDevices";
import { PMSLocationReservations } from "./PMSLocationReservations";
import { PMSLocationConfiguration } from "./PMSLocationConfiguration";
import { PMSLocationAlarm } from "./PMSLocationAlarm";
import { PMSLocationPublicLink } from "./PMSLocationPublicLink";
import { PMSLocationInvitations } from "./PMSLocationInvitations";
import { IntegrationConfig } from "./_components/IntegrationConfig";
import { VACATION_RENTAL_PLANS } from "../_constants/vacationRentalPlan.constants";
import { EARLY_CHECKIN_STATE_BADGE } from "../_models/PMSIntegration";
import organizationsIcon from "../../Common/_assets/icons/icon_module_organizations.svg";
import { LOCATION_PERMISSIONS } from "../../Everou/_constants/permissions.constants";
import { invitationPermissionUiValues } from "../../Everou/_constants/invitations.constants";

export function PMSLocationDetail() {

    // LIBRARIES
    const dispatch = useDispatch();
    const { t } = useTranslation();

    // GLOBAL STATE
    let locationInfo = LocationInfo();
    locationInfo = useSelector(Selectors.getPMSLocationInfo);
    const locationName = useSelector(Selectors.getPMSLocationSelectedName);
    const viewMode = useSelector(Selectors.getPMSLocationDetailViewMode);
    const locationUid = useSelector(Selectors.getPMSLocationSelectedUid);
    // Whether the location has a PMS is already known from the locations list
    // (has_integration field). We seed off it so the badge/redirect are right
    // immediately, without waiting for the per-location integrations fetch —
    // deriving them from that fetch flashed a wrong "no PMS" while the previous
    // location's stale, empty integrations lingered.
    const listHasIntegration = useSelector(Selectors.getPMSLocationSelectedHasIntegration);
    // Same seeding as the tab selector: the plan is known from the list on
    // selection, so a Premium tab never renders its empty fallback while
    // location/info is still in flight.
    const selectedVacationRentalPlan = useSelector(Selectors.getPMSLocationSelectedVacationRentalPlan);
    const selectedOrganizationName = useSelector(Selectors.getPMSLocationSelectedOrganizationName);
    const vacationRentalPlan = selectedVacationRentalPlan ?? locationInfo?.vacationRentalPlan;
    const pendingEarlyCheckinCount = useSelector(Selectors.getPMSLocationSelectedPendingEarlyCheckinCount);
    const locationIntegrations = useSelector(Selectors.getPMSLocationIntegrations);
    // The caller's role comes with the locations list (permission_type). Owners
    // get no badge: that is the usual case and the label would only be noise.
    // Admins and installers see theirs, so they know why some actions are
    // missing before they go looking for them.
    const permissionType = useSelector(Selectors.getPMSLocationSelectedPermissionType);
    const roleLabel = permissionType != null && permissionType !== LOCATION_PERMISSIONS.OWNER
        ? invitationPermissionUiValues[permissionType]
        : null;

    // LOCAL STATE
    // Which location the loaded integrations belong to. Tracking the uid (rather
    // than a bare boolean) avoids treating the previous location's integrations
    // as if they were this one's during a fast switch.
    const [integrationsLoadedUid, setIntegrationsLoadedUid] = useState<string | null>(null);

    // ACTION HANDLERS


    // INITIALIZATION
    useEffect(() => {
        let cancelled = false;
        dispatch(PMSLocationsActions.getLocationInfo());
        // Needed by the header, which paints this location's colours and logo
        // while it is open — not just by the Configuration tab.
        dispatch(PMSLocationsActions.getCustomization());

        // Pick the landing tab once per location, from the value already known
        // from the list: no PMS → Integrations so the user can connect one right
        // away; otherwise the default tab. This must run on location change only
        // (not when integrations change in-detail) so adding/removing one doesn't
        // yank the user off the tab they're on — and because the view mode is
        // global, it also keeps a no-PMS location from leaving the next (PMS) one
        // stuck on Integrations.
        dispatch(viewModeActions.changeMode(
            listHasIntegration === false
                ? PMSLocationDetailViewModes.INTEGRATIONS
                : PMSLocationDetailViewModes.RESERVATIONS,
        ));

        (async () => {
            await dispatch(PMSLocationsActions.getIntegrations());
            if (!cancelled) setIntegrationsLoadedUid(locationUid);
        })();
        return () => { cancelled = true; };
        // listHasIntegration is read once per location entry on purpose.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, locationUid]);

    // Before this location's integrations have loaded, trust the list's flag so
    // nothing flashes; afterwards trust the live count so adding/removing a PMS
    // in-detail updates the badge instantly.
    const showNoPms = integrationsLoadedUid === locationUid
        ? locationIntegrations.length === 0
        : listHasIntegration === false;

    return (
        <MainPageWithButtons
            size={"X_LARGE"}
            pageTitle={
                <span className="d-inline-flex align-items-center">
                    {locationName}
                    {locationInfo?.uid === locationUid && locationInfo?.locationId &&
                        <span
                            className="text-muted ml-3 pl-3 border-left"
                            style={{ textTransform: "none" }}
                        >
                            ID {locationInfo.locationId}
                        </span>
                    }
                    {selectedOrganizationName &&
                        <span
                            className="d-inline-flex align-items-center text-muted ml-3 pl-3 border-left"
                            style={{ textTransform: "none" }}
                        >
                            <img
                                className="c-img-1-25rem mr-1"
                                src={organizationsIcon}
                                alt=""
                            />
                            {selectedOrganizationName}
                        </span>
                    }
                    {(roleLabel || showNoPms || pendingEarlyCheckinCount > 0) &&
                        // The hairline goes on a wrapper, not on the badges:
                        // on the badge itself it would sit against its
                        // coloured background and pad it from the inside.
                        <span className="d-inline-flex align-items-center ml-3 pl-3 border-left">
                            {roleLabel &&
                                <span
                                    className="badge badge-secondary"
                                    style={{ textTransform: "none" }}
                                    data-testid="location-role-badge"
                                >
                                    {roleLabel}
                                </span>
                            }
                            {showNoPms &&
                                <span
                                    className={`badge badge-warning ${roleLabel ? "ml-2" : ""}`}
                                    style={{ textTransform: "none" }}
                                >
                                    {t("mod_pms_location_no_pms_badge")}
                                </span>
                            }
                            {pendingEarlyCheckinCount > 0 &&
                                <span
                                    className={`badge badge-pill ${roleLabel || showNoPms ? "ml-2" : ""}`}
                                    style={{
                                        backgroundColor: EARLY_CHECKIN_STATE_BADGE.requested.color,
                                        color: "#fff",
                                        textTransform: "none",
                                    }}
                                    title={t("mod_pms_location_pending_early_checkin_tooltip", {
                                        count: pendingEarlyCheckinCount,
                                    })}
                                >
                                    {t("mod_pms_location_pending_early_checkin_badge")}
                                    {pendingEarlyCheckinCount > 1 && ` · ${pendingEarlyCheckinCount}`}
                                </span>
                            }
                        </span>
                    }
                </span>
            }
            content={
                <div className="d-flex flex-column h-100">
                    <div className="pb-3">
                        <PMSLocationDetailSelector />
                    </div>
                    <div className="flex-fill overflow-auto">
                        {content()}
                    </div>
                </div>
            }
        />
    );

    function content() {
        switch (viewMode) {

            case PMSLocationDetailViewModes.DEVICES: {
                return (
                    <div className="d-flex flex-column justify-content-between h-100">
                        <PMSLocationDevices />
                    </div>
                )
            }

            case PMSLocationDetailViewModes.AUDIT: {
                if (vacationRentalPlan !== VACATION_RENTAL_PLANS.PREMIUM_PLAN) break;
                return <PMSLocationAuditLogs />
            }

            case PMSLocationDetailViewModes.RESERVATIONS: {
                return (
                    <div className="d-flex flex-column justify-content-between h-100">
                        <PMSLocationReservations />
                    </div>
                )
            }

            case PMSLocationDetailViewModes.INTEGRATIONS: {
                return (
                    <div className="d-flex flex-column justify-content-between h-100">
                        <IntegrationConfig />
                    </div>
                )
            }

            case PMSLocationDetailViewModes.PUBLIC_LINK_DEVICES: {
                if (vacationRentalPlan !== VACATION_RENTAL_PLANS.PREMIUM_PLAN) break;
                return (
                    <div className="d-flex flex-column justify-content-between h-100">
                        <PMSLocationPublicLink />
                    </div>
                )
            }

            case PMSLocationDetailViewModes.ALARM: {
                if (vacationRentalPlan !== VACATION_RENTAL_PLANS.PREMIUM_PLAN) break;
                return (
                    <div className="d-flex flex-column justify-content-between h-100">
                        <PMSLocationAlarm />
                    </div>
                )
            }

            case PMSLocationDetailViewModes.INVITATIONS: {
                if (vacationRentalPlan !== VACATION_RENTAL_PLANS.PREMIUM_PLAN) break;
                return (
                    <div className="d-flex flex-column justify-content-between h-100">
                        <PMSLocationInvitations />
                    </div>
                )
            }

            case PMSLocationDetailViewModes.CONFIGURATION: {
                return (
                    <div className="d-flex flex-column justify-content-between h-100">
                        <PMSLocationConfiguration />
                    </div>
                )
            }
        }
        return (
            <div className="d-flex flex-column justify-content-between h-100">
                <PMSLocationLogs />
            </div>
        )
    }
}